import type { TourData } from '@/components/TourMode/const';
import { create } from 'zustand';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import { clearTourDataCache } from '@/services/tour';
import { isProjectCompleted, markProjectCompleted } from './helper';

// ==================== Store 定义 ====================

interface TourState {
  /** 邀请卡片是否显示 */
  showTourPrompt: boolean;
  /** 是否正在请求导览数据 */
  isFetchingTour: boolean;
  /** 导览覆盖层是否显示 */
  showTour: boolean;
  /** 当前导览步骤索引 */
  tourStepIndex: number;
  /** 导览数据 */
  tourData: TourData | null;
  /** 生成导览数据时对应的预览文档；iframe 修复重载后用于识别旧数据 */
  tourDocumentKey: string | null;
  /** 生成当前导览文案时使用的界面语言 */
  tourLanguage: string | null;
  /** 标注数量 */
  annotationCount: number;
  /** 是否已展示过导览（防重复触发） */
  hasShownTour: boolean;

  setShowTourPrompt: (show: boolean) => void;
  setIsFetchingTour: (fetching: boolean) => void;
  setShowTour: (show: boolean) => void;
  setTourStepIndex: (index: number) => void;
  setTourData: (data: TourData | null, documentKey?: string | null, language?: string | null) => void;
  setAnnotationCount: (count: number) => void;
  setHasShownTour: (shown: boolean) => void;

  /** 完成导览并持久化 */
  completeTour: (projectId: string) => void;
  /** 检查项目是否已完成导览 */
  isProjectTourCompleted: (projectId: string) => boolean;
  /** 重置所有状态 */
  reset: () => void;
}

function getInitialState() {
  return {
    showTourPrompt: false,
    isFetchingTour: false,
    showTour: false,
    tourStepIndex: 0,
    tourData: null,
    tourDocumentKey: null,
    tourLanguage: null,
    annotationCount: 0,
    hasShownTour: false,
  };
}

export const useTourStore = create<TourState>((set) => ({
  ...getInitialState(),

  setShowTourPrompt: showTourPrompt => set({ showTourPrompt }),
  setIsFetchingTour: isFetchingTour => set({ isFetchingTour }),
  setShowTour: showTour => set({ showTour }),
  setTourStepIndex: tourStepIndex => set({ tourStepIndex }),
  setTourData: (tourData, tourDocumentKey = null, tourLanguage = null) => set({
    tourData,
    tourDocumentKey: tourData ? tourDocumentKey : null,
    tourLanguage: tourData ? tourLanguage : null,
  }),
  setAnnotationCount: annotationCount => set({ annotationCount }),
  setHasShownTour: hasShownTour => set({ hasShownTour }),

  completeTour: (projectId: string) => {
    markProjectCompleted(projectId);
    clearTourDataCache(projectId);
    set({
      isFetchingTour: false,
      showTour: false,
      showTourPrompt: false,
      hasShownTour: true,
    });
    globalEventEmitter.emit(EVENTS.INITIAL_GUIDE_FINISHED);
  },

  isProjectTourCompleted: (projectId: string) => {
    return isProjectCompleted(projectId);
  },

  reset: () => set(getInitialState()),
}));
